import React from "react";
import { TFunction } from "i18next";
import { TrashIcon } from "@aureskonnect/react-ui";
import { Tooltip } from "@mui/material";
import classnames from "classnames";
import { useDispatch } from "react-redux";

import ErrorToast from "@components/Common/ErrorTost";
import { CESAR_LOYALTY } from "@constants/index";
import { onKeyboardActivate } from "@helpers/a11y";

import { AppDispatch } from "../../../../../../redux/store";
import {
    setIsActionsClicked,
    setIsEdited,
    setArchiveMessage,
} from "../../../../../../redux/slices/fidelitySlice";
import { useFidelityNavigationGuard } from "../../../FidelityNavigationGuardContext";

type Props = {
    row: FidelityRow;
    oneShop: boolean;
    isEdited: boolean;
    isActionsClicked: boolean;
    isLoading: boolean;
    dataAssociate: string[];
    setSelectedRows: (rows: FidelityRow[]) => void;
    setIsAddFidelityClicked: (v: boolean) => void;
    setIsFidelityClicked: (v: boolean) => void;
    setIsModalOpenedSupport: (v: boolean) => void;
    setIsArchiveModalOpened: (v: boolean) => void;
    t: TFunction;
};

export default function ArchiveIconButton({
    row,
    oneShop,
    isEdited,
    isActionsClicked,
    isLoading,
    dataAssociate,
    setSelectedRows,
    setIsAddFidelityClicked,
    setIsFidelityClicked,
    setIsModalOpenedSupport,
    setIsArchiveModalOpened,
    t,
}: Props) {
    const dispatch = useDispatch<AppDispatch>();
    const { setOnConfirm } = useFidelityNavigationGuard();
    const title = row[t("State")] === true && oneShop ? "" : t("Archive");

    const setArchiveMessageIfAssociated = () => {
        if (dataAssociate.includes(row.Id)) {
            dispatch(
                setArchiveMessage(
                    t(
                        "Please note, this loyalty setting is assigned to one or more stores. Do you want to confirm the archiving?"
                    )
                )
            );
        }
    };

    const handleClick = () => {
        if (oneShop) {
            ErrorToast(
                t(
                    "Please note, loyalty archiving is done exclusively by the franchise."
                )
            );
            return;
        }
        setSelectedRows([]);
        setIsFidelityClicked(true);
        setArchiveMessageIfAssociated();

        if (row.fidelityChoiceIndex === CESAR_LOYALTY) {
            setIsModalOpenedSupport(true);
            return;
        }

        if (isEdited) {
            dispatch(setIsActionsClicked(!isActionsClicked));
            setOnConfirm(() => {
                dispatch(setIsEdited(false));
                setIsAddFidelityClicked(false);
                setIsArchiveModalOpened(true);
            });
        } else {
            setIsAddFidelityClicked(false);
            setIsArchiveModalOpened(true);
        }
    };

    const className = classnames(
        "pointer__clz cmn_icn_FRarchived",
        oneShop ? "mr-2" : "ml-3",
        { "not-allowed-icon__clz": isLoading }
    );

    return (
        <Tooltip title={title}>
            <div
                data-testid={`fidelity-franchise-archive-icon-${row.Id}`}
                role="button"
                tabIndex={0}
                aria-label={title || t("Archive")}
                onKeyDown={onKeyboardActivate(handleClick)}
            >
                <TrashIcon
                    onClick={handleClick}
                    height={25}
                    width={25}
                    fill="red"
                    style={{ cursor: "pointer" }}
                    className={className}
                />
            </div>
        </Tooltip>
    );
}
